import React, { useRef } from 'react';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import PolarisIcon from '@/components/_common/PolarisIcon';
import axios from '@/_utils/axios';
import { useToast } from '@/components/_common/Toast';

interface Props {
  onDeleted: () => void;
  onDeleteFailed?: () => void;
}

const useDeleteTemplateConfirmation = ({
  onDeleted,
  onDeleteFailed
}: Props): {
  deleteTemplateDialog: React.ReactElement;
  openDialog: (templateId: number) => void;
} => {
  const templateIdRef = useRef<number | null>(null);
  const { error } = useToast();

  const { dialog: deleteTemplateDialog, setDialogVisible } = useConfirmationPrompt({
    title: 'Confirm Template Delete',
    titleIcon: <PolarisIcon icon="delete" height={19} style={{ opacity: 0.5, marginRight: 8 }} />,
    message: 'Are you sure you want to delete this template?',
    onConfirm: () => {
      if (templateIdRef.current === null) {
        throw new Error('templateId is missing');
      }

      return axios
        .delete(`assembly_templates/${templateIdRef.current}/`)
        .then(() => {
          templateIdRef.current = null;
          onDeleted();
        })
        .catch(() => {
          if (onDeleteFailed) onDeleteFailed();
          error('Error deleting template');
        });
    },
    onCancel: () => {
      templateIdRef.current = null;
    },
    buttonColor: 'error',
    buttons: {
      submitText: 'Delete Template',
      cancelText: 'No'
    }
  });

  const openDialog = (templateId: number) => {
    templateIdRef.current = templateId;
    setDialogVisible(true);
  };

  return {
    deleteTemplateDialog,
    openDialog
  };
};

export default useDeleteTemplateConfirmation;
